import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Inventory2 as PalletIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import TsdPageBar, { TsdCabinetSelect } from '../components/tsd/TsdPageBar';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import { useNotify } from '../components/NotifyProvider';
import usePrinter from '../hooks/usePrinter';
import useFulfillmentCabinet from '../hooks/useFulfillmentCabinet';
import { newPrintIdempotencyKey } from '../utils/printClient';
import { getWbImageUrl, toThumbnail } from '../utils/productImage';
import {
  fulfillmentApiBase,
  labelingConfirmLabel,
  labelingCreatePalletBody,
  labelingOrderBody,
  labelingScopeCompatible,
  labelingSupportsCancel,
  labelingVerifyBody,
  normalizeLabelingQueueRow,
  normalizeLabelingResolve,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import { readPalletPrintAfterClose } from './fulfillmentUi.mjs';
import PalletCloseDialog from '../components/fulfillment/PalletCloseDialog';

const ACTIVE_PALLET_KEY = 'labeling_active_pallet_v2';

const TABS = [
  { key: 'new', label: 'Новые' },
  { key: 'errors', label: 'С ошибками' },
];

function errorText(error) {
  const data = error?.response?.data;
  if (!error?.response
    && (error?.code === 'ECONNABORTED' || error?.message === 'Network Error')) {
    return 'Нет связи с сервером. Проверьте Wi-Fi и повторите';
  }
  const base = data?.message || error.message || 'Не удалось выполнить операцию';
  const departed = data?.details?.departed_orders;
  if (!Array.isArray(departed) || !departed.length) return base;
  const lines = departed.slice(0, 12).map((row) => {
    const id = row.posting_number ?? row.wb_order_id ?? '—';
    const article = row.article ? ` · ${row.article}` : '';
    const reason = row.reason || 'выбыл';
    return `${id}${article} — ${reason}`;
  });
  const more = departed.length > 12 ? `\n…и ещё ${departed.length - 12}` : '';
  return `${base}\n${lines.join('\n')}${more}`;
}

/**
 * Палета стала непригодной (закрыл другой ТСД/АРМ, не та палета) — продолжать
 * с ней нельзя, активную надо сбросить и попросить новую.
 */
const PALLET_UNUSABLE_CODES = Object.freeze({
  pallet_closed: 'закрыта другим сотрудником',
  pallet_not_found: 'больше недоступна',
  pallet_mismatch: 'не подходит для этого заказа',
});

function palletUnusableCode(error) {
  const code = error?.response?.data?.code;
  return Object.hasOwn(PALLET_UNUSABLE_CODES, code) ? code : null;
}

function palletUnusableText(code, pallet) {
  const label = pallet?.code || (pallet?.id ? `#${pallet.id}` : '');
  const reason = PALLET_UNUSABLE_CODES[code] || 'недоступна';
  return `Палета ${label} ${reason}. Отсканируйте или создайте новую`.replace('  ', ' ');
}

/**
 * Вкладка, на которой заказ на самом деле лежит: WB отдаёт `details.tab`,
 * Ozon/Yandex — `validation_error` с `details.code === 'wrong_tab'`.
 */
function wrongTabFrom(error) {
  const data = error?.response?.data;
  const details = data?.details || {};
  const isWrongTab = data?.code === 'wrong_tab' || details.code === 'wrong_tab';
  if (!isWrongTab) return null;
  if (details.tab === 'new' || details.tab === 'errors') return details.tab;
  if (details.composition_alert != null) return details.composition_alert ? 'errors' : 'new';
  return null;
}

/** Ошибки, после которых заказ действительно надо увести в «С ошибками». */
const FATAL_FLOW_CODES = new Set([
  'already_labeled',
  'orders_left_mp',
  'labeling_not_eligible',
]);

/** Неверный ШК / палета — оставить диалог сверки, не уводить заказ в ошибки. */
function isSoftVerifyError(error) {
  const code = error?.response?.data?.code;
  const status = Number(error?.response?.status);
  if (
    code === 'sticker_mismatch'
    || code === 'sticker_already_used'
    || code === 'barcode_mismatch'
    || code === 'validation_error'
    || code === 'pallet_required'
    || code === 'pallet_mismatch'
    || code === 'pallet_closed'
    || code === 'pallet_not_found'
    || code === 'pallet_barcode_mismatch'
  ) {
    return true;
  }
  if (status === 409) {
    const fatal = new Set([
      'orders_left_mp',
      'already_labeled',
      'sticker_missing',
      'labeling_not_eligible',
    ]);
    return !fatal.has(code);
  }
  return false;
}

function storageSlot(marketplace, accountId) {
  return `${marketplace || 'wb'}:${accountId}`;
}

function readStoredPallet(marketplace, accountId) {
  try {
    const raw = localStorage.getItem(ACTIVE_PALLET_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map?.[storageSlot(marketplace, accountId)] || null;
  } catch {
    return null;
  }
}

function writeStoredPallet(marketplace, accountId, pallet) {
  try {
    const raw = localStorage.getItem(ACTIVE_PALLET_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const key = storageSlot(marketplace, accountId);
    if (!pallet) delete map[key];
    else {
      map[key] = {
        id: pallet.id,
        code: pallet.code,
        barcode: pallet.barcode,
        items_count: pallet.items_count,
        warehouse_id: pallet.warehouse_id,
        cargo_type: pallet.cargo_type,
        cross_border_type: pallet.cross_border_type,
        delivery_method_id: pallet.delivery_method_id,
        account_id: pallet.account_id,
      };
    }
    localStorage.setItem(ACTIVE_PALLET_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function labelingProducts(task, marketplace) {
  if (marketplace !== 'yandex') return null;
  return Array.isArray(task?.products) && task.products.length ? task.products : null;
}

function ProductThumb({ task, marketplace }) {
  const preferred = task?.image_url ? toThumbnail(task.image_url) : null;
  const fallback = (marketplace === 'ozon' || marketplace === 'yandex') ? null : getWbImageUrl(task?.nm_id);
  const [src, setSrc] = useState(preferred || fallback);

  useEffect(() => {
    setSrc(preferred || fallback);
  }, [preferred, fallback]);

  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setSrc((current) => (fallback && current !== fallback ? fallback : null))}
      sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }}
    />
  );
}

function ProductIdentity({ task, marketplace, align = 'left', titleVariant = 'titleSmall', detail = false }) {
  const products = labelingProducts(task, marketplace);
  const article = task?.article ? String(task.article) : null;
  const nmId = task?.nm_id != null && task.nm_id !== '' ? String(task.nm_id) : null;
  const barcode = task?.barcode ? String(task.barcode) : null;
  const orderId = task?.wb_order_id != null && task.wb_order_id !== ''
    ? String(task.wb_order_id)
    : (task?.posting_number != null ? String(task.posting_number) : null);
  if (products) {
    const places = marketplace === 'yandex' && Array.isArray(task?.cargo_places)
      ? task.cargo_places
      : null;
    const currentCode = task?.cargo_place_current?.code;
    const verifiedCount = Number(task?.labels_verified);
    const totalCount = Number(task?.labels_total);
    const showPlaces = Boolean(places && places.length);
    return (
      <Box sx={{ minWidth: 0, textAlign: align }}>
        <Typography variant={titleVariant} noWrap={align === 'left'}>
          {orderId ? `Заказ ${orderId}` : 'Заказ'}
        </Typography>
        {showPlaces && (
          <Typography variant="bodySmall" color="text.secondary" noWrap={align === 'left'}>
            Грузоместа {Number.isFinite(verifiedCount) ? verifiedCount : 0} из {totalCount}
            {currentCode ? ` · сейчас ${currentCode}` : ''}
          </Typography>
        )}
        {showPlaces && detail && (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent={align === 'center' ? 'center' : 'flex-start'} sx={{ mt: 0.5 }}>
            {places.map((place) => (
              <Chip
                key={place.code || place.index}
                size="small"
                label={place.code}
                color={place.verified ? 'success' : (place.code === currentCode ? 'warning' : 'default')}
              />
            ))}
          </Stack>
        )}
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {products.map((line) => {
            const need = Number(line.qty_need || line.quantity || 1);
            return (
              <Stack
                key={line.key || line.offer_id || line.sku || line.article}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={align === 'center' ? 'center' : 'flex-start'}
              >
                {detail && <ProductThumb task={line} marketplace={marketplace || 'yandex'} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="bodyMedium" sx={{ wordBreak: 'break-word' }}>
                    {line.article || line.offer_id || 'Товар'}
                    {' · '}
                    {need}
                    {' шт'}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  }
  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <Typography variant={titleVariant} noWrap={align === 'left'}>
        {article || 'Товар'}
      </Typography>
      {nmId && (
        <Typography variant="bodySmall" color="text.secondary" noWrap={align === 'left'}>
          nmID {nmId}
        </Typography>
      )}
      {barcode && (
        <Typography variant="bodySmall" color="text.secondary" noWrap={align === 'left'}>
          Баркод {barcode}
        </Typography>
      )}
      {orderId && (
        <Typography variant="bodySmall" color="text.secondary" noWrap={align === 'left'}>
          Заказ {orderId}
        </Typography>
      )}
    </Box>
  );
}

export default function FulfillmentLabeling() {
  const scanLock = useRef(false);
  const printer = usePrinter();
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const { success: notifySuccess, error: notifyError } = useNotify();
  const {
    accounts,
    accountsLoading,
    accountsError,
    accountId,
    setAccountId,
    marketplace,
    isOzon,
    isYandex,
    isLocalWarehouse,
  } = useFulfillmentCabinet();

  const apiBase = fulfillmentApiBase(marketplace);
  const canCancel = labelingSupportsCancel(marketplace);

  const [tab, setTab] = useState('new');
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState({});
  const [queueLoading, setQueueLoading] = useState(false);

  const [activePallet, setActivePallet] = useState(null);
  /** Синхронное зеркало активной палеты: async-шаги попапа не должны видеть stale-стейт. */
  const activePalletRef = useRef(null);
  const palletCheckedAtRef = useRef(0);
  const [palletOpenMode, setPalletOpenMode] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeStep, setCloseStep] = useState('confirm');
  const [closeVerifyCode, setCloseVerifyCode] = useState('');
  const [closeError, setCloseError] = useState('');
  const [cancelPalletOpen, setCancelPalletOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [palletNotice, setPalletNotice] = useState('');
  const [palletSwitch, setPalletSwitch] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const [dropFlowOpen, setDropFlowOpen] = useState(false);

  const [popupOpen, setPopupOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [labelingStatus, setLabelingStatus] = useState(null);
  /** Только отказ постановки в очередь (print_error от prepare) или 409 sticker_missing — не результат печати. */
  const [printError, setPrintError] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');

  useEffect(() => {
    const forget = () => {
      activePalletRef.current = null;
      setActivePallet(null);
    };
    if (!accountId) {
      forget();
      return undefined;
    }
    const stored = readStoredPallet(marketplace, accountId);
    if (!stored?.id) {
      forget();
      return undefined;
    }
    // Ответ по прежнему кабинету не должен записать палету в новый.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${apiBase}/pallets/${stored.id}`, {
          params: withAccountParams(marketplace, accountId),
          silent: true,
        });
        if (cancelled) return;
        if (data?.status === 'open') {
          activePalletRef.current = data;
          setActivePallet(data);
          writeStoredPallet(marketplace, accountId, data);
        } else {
          forget();
          writeStoredPallet(marketplace, accountId, null);
        }
      } catch {
        if (cancelled) return;
        forget();
        writeStoredPallet(marketplace, accountId, null);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, apiBase, marketplace]);

  const loadQueue = useCallback(async () => {
    if (!accountId) {
      setQueue([]);
      setCounts({});
      return;
    }
    setQueueLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/labeling/queue`, {
        params: withAccountParams(marketplace, accountId, { filter: tab, tab }),
        silent: true,
      });
      const items = (data?.items || [])
        .map((row) => normalizeLabelingQueueRow(row, marketplace))
        .filter(Boolean);
      setQueue(items);

      if (data?.counts && typeof data.counts === 'object') {
        setCounts(data.counts);
      } else if (isLocalWarehouse) {
        const otherTab = tab === 'new' ? 'errors' : 'new';
        const other = await axios.get(`${apiBase}/labeling/queue`, {
          params: withAccountParams(marketplace, accountId, {
            filter: otherTab,
            tab: otherTab,
            page_size: 1,
          }),
          silent: true,
        });
        setCounts({
          [tab]: data?.total ?? items.length,
          [otherTab]: other.data?.total ?? 0,
        });
      } else {
        setCounts({});
      }
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setQueueLoading(false);
    }
  }, [accountId, tab, apiBase, marketplace, isLocalWarehouse]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const selectPallet = useCallback((pallet) => {
    activePalletRef.current = pallet || null;
    setActivePallet(pallet);
    if (accountId) writeStoredPallet(marketplace, accountId, pallet);
    setPalletOpenMode(false);
    setPalletNotice('');
  }, [accountId, marketplace]);

  /** Палету закрыли/удалили в другом месте — снимаем её и просим новую. */
  const dropActivePallet = useCallback((code, pallet) => {
    activePalletRef.current = null;
    setActivePallet(null);
    if (accountId) writeStoredPallet(marketplace, accountId, null);
    setPalletOpenMode(false);
    setPalletNotice(palletUnusableText(code, pallet));
  }, [accountId, marketplace]);

  /**
   * Дешёвая сверка статуса активной палеты перед verify-attach и при возврате
   * вкладки в фокус. Возвращает актуальную палету или null, если она непригодна.
   */
  const refreshActivePallet = useCallback(async (pallet) => {
    if (!pallet?.id) return null;
    try {
      const { data } = await axios.get(`${apiBase}/pallets/${pallet.id}`, {
        params: withAccountParams(marketplace, accountId),
        silent: true,
      });
      if (data?.status !== 'open') {
        dropActivePallet(data?.status === 'closed' ? 'pallet_closed' : 'pallet_not_found', pallet);
        return null;
      }
      activePalletRef.current = data;
      setActivePallet(data);
      if (accountId) writeStoredPallet(marketplace, accountId, data);
      return data;
    } catch (requestError) {
      if (Number(requestError?.response?.status) === 404) {
        dropActivePallet('pallet_not_found', pallet);
        return null;
      }
      // Сетевой сбой проверки не должен блокировать работу — пробуем verify как есть.
      return pallet;
    }
  }, [accountId, apiBase, marketplace, dropActivePallet]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!activePalletRef.current?.id) return;
      if (Date.now() - palletCheckedAtRef.current < 30000) return;
      palletCheckedAtRef.current = Date.now();
      void refreshActivePallet(activePalletRef.current);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshActivePallet]);

  const closePopup = useCallback(() => {
    setPopupOpen(false);
    setSelected(null);
    setLabelingStatus(null);
    setPrintError(null);
    setVerifyCode('');
    setRetryable(false);
    setPalletSwitch(null);
    setDropFlowOpen(false);
  }, []);

  /**
   * Решение владельца 2026-10-01: ТСД не ждёт результат печати и не сообщает о нём.
   * Оператор видит этикетку глазами, а ненапечатанную подтвердить не может —
   * сверка идёт сканом наклейки. Поэтому после постановки в очередь — сразу шаг
   * «Этикетка наклеена»; статусы print_jobs (completed/unknown/failed) ТСД не читает.
   */
  const afterPrintEnqueued = useCallback(() => {
    setPrintError(null);
    setLabelingStatus('awaiting_confirm');
  }, []);

  const openFlow = useCallback((payload, scannedCode = '') => {
    const candidate = payload?.selected || {
      task: payload.task,
      job: payload.job,
      item: payload.item,
    };
    const status = payload.status || 'awaiting_label';
    const matched = String(
      payload?.verify_prefill != null ? payload.verify_prefill : scannedCode || '',
    ).trim();
    setSelected(candidate);
    setLabelingStatus(status);
    setPrintError(null);
    // Yandex: товарный ШК для поиска заказа не должен попадать в сверку грузоместа.
    setVerifyCode(status === 'awaiting_verify' && matched ? matched : '');
    setError('');
    setNotice('');
    setRetryable(false);
    setPopupOpen(true);
  }, []);

  const palletFitsOrder = useCallback((pallet, task) => {
    if (isOzon) {
      return labelingScopeCompatible(pallet.delivery_method_id, task.delivery_method_id)
        && labelingScopeCompatible(pallet.warehouse_id, task.warehouse_id);
    }
    if (isYandex) {
      return labelingScopeCompatible(pallet.warehouse_id, task.warehouse_id);
    }
    // WB: бэкенд сверяет склад + тип груза + cross_border_type.
    return String(pallet.warehouse_id ?? '') === String(task.warehouse_id ?? '')
      && String(pallet.cargo_type ?? '') === String(task.cargo_type ?? '')
      && String(pallet.cross_border_type ?? 0) === String(task.cross_border_type ?? 0);
  }, [isOzon, isYandex]);

  const createPalletForOrder = async (task) => {
    const { data } = await axios.post(
      `${apiBase}/pallets`,
      labelingCreatePalletBody(marketplace, accountId, task),
    );
    selectPallet(data);
    return data;
  };

  /**
   * Возвращает палету для заказа или `null`, если нужен явный выбор оператора:
   * молча подменять активную палету нельзя — заказ уедет не на ту палету.
   */
  const ensurePalletForOrder = async (task, resume = null) => {
    const current = activePalletRef.current;
    if (current?.id) {
      if (palletFitsOrder(current, task)) return current;
      setPalletSwitch({ task, pallet: current, resume });
      return null;
    }
    return createPalletForOrder(task);
  };

  // silent: wrong_tab гасим авто-переключением, тост показываем только на финальной ошибке.
  const requestResolve = async (code, tabKey) => {
    const { data } = await axios.post(
      `${apiBase}/labeling/scan/resolve`,
      withAccountBody(marketplace, accountId, {
        barcode: code, code, filter: tabKey, tab: tabKey,
      }),
      { silent: true },
    );
    return normalizeLabelingResolve(data, marketplace, code);
  };

  const resolveScan = async (raw) => {
    const code = String(raw || '').trim();
    if (!code || busy || scanLock.current) return;

    if (closeOpen && closeStep === 'verify') {
      setCloseVerifyCode(code);
      setCloseError('');
      setError('');
      return;
    }
    if (closeOpen) {
      return;
    }

    if (popupOpen && labelingStatus === 'awaiting_verify') {
      setVerifyCode(code);
      setError('');
      return;
    }
    if (popupOpen) {
      return;
    }

    if (palletOpenMode) {
      await resolvePalletScan(code);
      return;
    }

    scanLock.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let data;
      try {
        data = await requestResolve(code, tab);
      } catch (requestError) {
        // Заказ на соседней вкладке — переключаемся и повторяем один раз.
        const nextTab = wrongTabFrom(requestError);
        if (!nextTab || nextTab === tab) throw requestError;
        setTab(nextTab);
        data = await requestResolve(code, nextTab);
      }
      if (accountId && data?.selected?.task?.account_id
        && Number(data.selected.task.account_id) !== Number(accountId)) {
        setAccountId(String(data.selected.task.account_id));
      }
      openFlow(data, code);
    } catch (requestError) {
      const msg = errorText(requestError);
      setError(msg);
      notifyError(msg);
    } finally {
      setBusy(false);
      scanLock.current = false;
    }
  };

  const cancelFlow = async () => {
    if (!selected?.task || !canCancel) {
      closePopup();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axios.post(`${apiBase}/labeling/cancel`, labelingOrderBody(marketplace, selected.task));
      setTab('errors');
      setNotice('Заказ в ошибках');
      closePopup();
      await loadQueue();
    } catch (requestError) {
      setError(errorText(requestError));
      setTab('errors');
      closePopup();
      await loadQueue();
    } finally {
      setBusy(false);
    }
  };

  const markErrorAndClose = async (message) => {
    setError(message || 'Сбой — заказ в ошибках');
    if (selected?.task && canCancel) {
      try {
        await axios.post(
          `${apiBase}/labeling/cancel`,
          labelingOrderBody(marketplace, selected.task),
          { silent: true },
        );
      } catch {
        /* already limbo */
      }
      setTab('errors');
    }
    closePopup();
    await loadQueue();
  };

  /**
   * Сетевой сбой или ошибка печати не должны увозить заказ в «С ошибками»:
   * попап остаётся на своём шаге с кнопкой «Повторить».
   */
  const handleFlowError = async (requestError) => {
    const msg = errorText(requestError);
    const code = requestError?.response?.data?.code;
    if (FATAL_FLOW_CODES.has(code)) {
      await markErrorAndClose(msg);
      return;
    }
    const unusable = palletUnusableCode(requestError);
    if (unusable) {
      dropActivePallet(unusable, activePallet);
      return;
    }
    setError(msg);
    setRetryable(true);
  };

  const prepareAndPrint = async () => {
    if (!selected?.task || busy) return;
    if (!printerUid) {
      // Без принтера WB создавал поставку и заказ без этикетки.
      const msg = 'Выберите принтер в настройках печати';
      setLabelingStatus('awaiting_label');
      setError(msg);
      notifyError(msg);
      return;
    }
    setBusy(true);
    setError('');
    setRetryable(false);
    try {
      const pallet = await ensurePalletForOrder(selected.task, 'prepare');
      if (!pallet) return;
      const prep = await axios.post(
        `${apiBase}/labeling/prepare`,
        labelingOrderBody(marketplace, selected.task, { printer_uid: printerUid }),
        { headers: { 'Idempotency-Key': newPrintIdempotencyKey('label-prepare') } },
      );

      if (prep.data?.task) {
        setSelected((prev) => ({ ...prev, task: prep.data.task }));
      } else if (prep.data?.cargo_places || prep.data?.cargo_place) {
        setSelected((prev) => ({
          ...prev,
          task: {
            ...(prev?.task || {}),
            cargo_places: prep.data.cargo_places || prev?.task?.cargo_places,
            cargo_place_current: prep.data.cargo_place || prev?.task?.cargo_place_current,
            labels_verified: prep.data.labels_verified ?? prev?.task?.labels_verified,
            labels_total: prep.data.labels_total ?? prev?.task?.labels_total,
          },
        }));
      }

      if (prep.data?.print_error) {
        // Печать не встала в очередь — «Этикетка наклеена» недоступна, только повтор печати.
        setPrintError(prep.data.print_error);
        setLabelingStatus('print_pending');
        setError(prep.data.print_error.message || 'Печать не удалась');
      } else if (prep.data?.print_job) {
        afterPrintEnqueued();
      } else {
        await axios.post(
          `${apiBase}/labeling/print`,
          labelingOrderBody(marketplace, selected.task, { printer_uid: printerUid }),
          { headers: { 'Idempotency-Key': newPrintIdempotencyKey('label-print') } },
        );
        afterPrintEnqueued();
      }
      await loadQueue();
    } catch (requestError) {
      await handleFlowError(requestError);
    } finally {
      setBusy(false);
    }
  };

  const reprint = async () => {
    if (!selected?.task || busy) return;
    if (!printerUid) {
      const msg = 'Выберите принтер в настройках печати';
      setError(msg);
      notifyError(msg);
      return;
    }
    setBusy(true);
    setError('');
    setRetryable(false);
    try {
      await axios.post(
        `${apiBase}/labeling/print`,
        labelingOrderBody(marketplace, selected.task, { printer_uid: printerUid }),
        { headers: { 'Idempotency-Key': newPrintIdempotencyKey('label-reprint') } },
      );
      afterPrintEnqueued();
    } catch (requestError) {
      // Отказ сервера/МП поставить печать в очередь — не результат печати, показываем как раньше.
      const msg = errorText(requestError);
      setError(msg);
      setPrintError({ message: msg });
      setLabelingStatus('print_pending');
      notifyError(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmApplied = async () => {
    if (!selected?.task || busy) return;
    setBusy(true);
    setError('');
    setRetryable(false);
    try {
      await axios.post(
        `${apiBase}/labeling/confirm-applied`,
        labelingOrderBody(marketplace, selected.task),
      );
      setLabelingStatus('awaiting_verify');
      setVerifyCode('');
      await loadQueue();
    } catch (requestError) {
      if (requestError?.response?.data?.code === 'sticker_missing') {
        // Этикетки нет — возвращаем оператора на шаг печати, а не в ошибки.
        setLabelingStatus('print_pending');
        setPrintError({ message: errorText(requestError) });
      }
      await handleFlowError(requestError);
    } finally {
      setBusy(false);
    }
  };

  const verifyAttach = async () => {
    const code = String(verifyCode || '').trim();
    if (!selected?.task || busy) return;
    if (!code) {
      setError(isYandex
        ? 'Сканируй этикетку грузоместа'
        : 'Сканируй этикетку заказа');
      return;
    }
    setBusy(true);
    setError('');
    setRetryable(false);
    try {
      let pallet = activePalletRef.current;
      if (pallet?.id) {
        // Палету мог закрыть другой ТСД/АРМ — сверяем до verify-attach.
        palletCheckedAtRef.current = Date.now();
        pallet = await refreshActivePallet(pallet);
        if (!pallet?.id) return;
        if (!palletFitsOrder(pallet, selected.task)) {
          setPalletSwitch({ task: selected.task, pallet, resume: 'verify' });
          return;
        }
      }
      if (!pallet?.id) {
        pallet = await ensurePalletForOrder(selected.task, 'verify');
        if (!pallet?.id) return;
      }
      const { data } = await axios.post(
        `${apiBase}/labeling/verify-attach`,
        labelingVerifyBody(marketplace, selected.task, { code, palletId: pallet.id }),
        { headers: { 'Idempotency-Key': newPrintIdempotencyKey('label-verify') } },
      );
      if (isYandex && data?.complete === false) {
        if (data?.pallet) selectPallet(data.pallet);
        setVerifyCode('');
        setNotice(data.notice
          || `Грузоместо ${data.cargo_place?.code || ''} на палете, осталось ${data.remaining_places ?? ''}`.trim());
        notifySuccess(data.notice
          || `Грузоместо ${data.cargo_place?.code || ''} на палете`);
        closePopup();
        await loadQueue();
        return;
      }
      if (data?.pallet) selectPallet(data.pallet);
      setNotice('Готово');
      closePopup();
      await loadQueue();
    } catch (requestError) {
      const msg = errorText(requestError);
      const unusable = palletUnusableCode(requestError);
      if (unusable) {
        // Стикер уже наклеен — попап остаётся на сверке, нужна только другая палета.
        dropActivePallet(unusable, activePallet);
        return;
      }
      if (isSoftVerifyError(requestError)) {
        setError(msg);
        notifyError(msg);
        setVerifyCode('');
        return;
      }
      await handleFlowError(requestError);
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (labelingStatus === 'awaiting_label') return prepareAndPrint();
    if (labelingStatus === 'print_pending') return reprint();
    if (labelingStatus === 'awaiting_confirm') return confirmApplied();
    if (labelingStatus === 'awaiting_verify') return verifyAttach();
    return undefined;
  };

  /** Оператор согласился сменить активную палету на подходящую заказу. */
  const confirmPalletSwitch = async () => {
    const pending = palletSwitch;
    if (!pending?.task || busy) return;
    setBusy(true);
    setError('');
    try {
      const created = await createPalletForOrder(pending.task);
      setPalletSwitch(null);
      const changed = `Палета для работы изменена на ${created.code || `#${created.id}`}`;
      setNotice(changed);
      notifySuccess(changed);
    } catch (requestError) {
      setPalletSwitch(null);
      setError(errorText(requestError));
      return;
    } finally {
      setBusy(false);
    }
    if (pending.resume === 'prepare') await prepareAndPrint();
    else if (pending.resume === 'verify') await verifyAttach();
  };

  /** «Снять с проклейки» — единственный путь, который уводит заказ в «С ошибками». */
  const dropFromLabeling = async () => {
    setDropFlowOpen(false);
    await cancelFlow();
  };

  const showReprint = labelingStatus === 'awaiting_confirm' || labelingStatus === 'print_pending';

  const resolvePalletScan = async (raw) => {
    const code = String(raw || '').trim();
    if (!code || busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets/resolve`,
        withAccountBody(marketplace, accountId, { barcode: code, code }),
      );
      selectPallet(data);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const openPallet = async () => {
    if (!accountId) {
      setError('Выберите кабинет');
      return;
    }
    const seed = queue.find((row) => row.task)?.task;
    if (!seed) {
      setPalletOpenMode(true);
      setError('Отсканируйте ШК палеты или сначала найдите заказ');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets`,
        labelingCreatePalletBody(marketplace, accountId, seed),
      );
      selectPallet(data);
    } catch (requestError) {
      setPalletOpenMode(true);
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  const printPalletLabel = async ({ skipBusyGuard = false } = {}) => {
    if (!activePallet?.id || (!skipBusyGuard && busy)) return;
    if (!printerUid) {
      const msg = 'Выберите принтер в настройках печати';
      setCloseError(msg);
      notifyError(msg);
      return;
    }
    setBusy(true);
    setCloseError('');
    try {
      // Результат печати не ждём и не показываем (как для этикетки заказа).
      await axios.post(
        `${apiBase}/pallets/${activePallet.id}/print`,
        withAccountBody(marketplace, accountId, { printer_uid: printerUid }),
        { headers: { 'Idempotency-Key': newPrintIdempotencyKey('pallet-print') } },
      );
      setNotice('Печать этикетки палеты отправлена');
      notifySuccess('Печать этикетки палеты отправлена');
    } catch (requestError) {
      const msg = errorText(requestError);
      setCloseError(msg);
      notifyError(msg);
    } finally {
      setBusy(false);
    }
  };

  const openCloseDialog = () => {
    setCloseStep('confirm');
    setCloseVerifyCode('');
    setCloseError('');
    setCloseOpen(true);
  };

  const cancelCloseDialog = () => {
    if (busy) return;
    setCloseOpen(false);
    setCloseStep('confirm');
    setCloseVerifyCode('');
    setCloseError('');
  };

  const reprintPalletLabel = async () => {
    await printPalletLabel();
  };

  /** Палету открыли по ошибке: пока на ней ничего нет — убрать её из работы. */
  const cancelActivePallet = async () => {
    if (!activePallet?.id || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${apiBase}/pallets/${activePallet.id}/cancel`,
        withAccountBody(marketplace, accountId),
      );
      setNotice(`Палета ${activePallet.code || activePallet.id} убрана`);
      notifySuccess('Палета убрана');
      selectPallet(null);
      setCancelPalletOpen(false);
      await loadQueue();
    } catch (requestError) {
      const msg = errorText(requestError);
      setError(msg);
      notifyError(msg);
      setCancelPalletOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const closeActivePallet = async (scanned) => {
    if (!activePallet?.id) return;
    const code = String(scanned || closeVerifyCode || '').trim();
    if (!code) {
      setCloseError('Сканируй ШК палеты');
      return;
    }
    setBusy(true);
    setCloseError('');
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets/${activePallet.id}/close`,
        withAccountBody(marketplace, accountId, {
          scanned_barcode: code,
          code,
        }),
      );
      setNotice(`Палета ${data.code || activePallet.code} закрыта`);
      notifySuccess(`Палета ${data.code || activePallet.code} закрыта`);
      selectPallet(null);
      setCloseOpen(false);
      setCloseStep('confirm');
      setCloseVerifyCode('');
      await loadQueue();
      if (readPalletPrintAfterClose() && printerUid) {
        await printPalletLabel({ skipBusyGuard: true });
      }
    } catch (requestError) {
      const msg = errorText(requestError);
      const unusable = palletUnusableCode(requestError);
      if (unusable) {
        // Палету закрыли параллельно — диалог закрытия больше не о чем.
        dropActivePallet(unusable, activePallet);
        setCloseOpen(false);
        setCloseStep('confirm');
        setCloseVerifyCode('');
        await loadQueue();
        return;
      }
      const soft = requestError?.response?.data?.code === 'pallet_barcode_mismatch'
        || requestError?.response?.data?.code === 'validation_error';
      if (soft) {
        setCloseError(msg);
        notifyError(msg);
        setCloseVerifyCode('');
        return;
      }
      setCloseError(msg);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const task = selected?.task;
  const scanHelper = closeOpen && closeStep === 'verify'
    ? 'Сканируйте ШК палеты'
    : (popupOpen && labelingStatus === 'awaiting_verify'
      ? (isYandex ? 'Сканируйте ШК грузоместа' : 'Сканируйте этикетку заказа')
      : (palletOpenMode
        ? 'Сканируйте ШК палеты'
        : (tab === 'errors' ? 'Сканируйте товар или этикетку' : 'Сканируйте товар')));

  return (
    <>
      <TsdPageBar
        title="Проклейка"
        extra={(
          <TsdCabinetSelect
            id="labeling-account"
            accounts={accounts}
            accountId={accountId}
            disabled={accountsLoading}
            onChange={(value) => {
              setAccountId(value);
              closePopup();
            }}
          />
        )}
      />
      <Stack spacing={2} sx={{ maxWidth: 720, mx: 'auto', pb: 4 }}>
        {(error || accountsError) && (
          <Alert severity="error" onClose={() => setError('')}>
            {error || accountsError}
          </Alert>
        )}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
        {palletNotice && (
          <Alert severity="warning" onClose={() => setPalletNotice('')}>{palletNotice}</Alert>
        )}

        {!accountsLoading && !accounts.length && (
          <Alert severity="warning">Нет доступных кабинетов</Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PalletIcon color={activePallet ? 'primary' : 'disabled'} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {activePallet ? (
                <Typography variant="titleMedium" noWrap>
                  {activePallet.code || `#${activePallet.id}`}
                  {' · '}
                  {activePallet.items_count || 0}
                </Typography>
              ) : (
                <Typography color="text.secondary">
                  {palletOpenMode ? 'Сканируйте ШК палеты' : 'Нет активной палеты'}
                </Typography>
              )}
            </Box>
            {activePallet ? (
              Number(activePallet.items_count) > 0 ? (
                <Button
                  color="warning"
                  variant="outlined"
                  disabled={busy || popupOpen}
                  onClick={openCloseDialog}
                  sx={{ minHeight: 48 }}
                >
                  Закрыть
                </Button>
              ) : (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={busy || popupOpen}
                  onClick={() => setCancelPalletOpen(true)}
                  sx={{ minHeight: 48 }}
                >
                  Убрать палету
                </Button>
              )
            ) : (
              <Button
                variant="contained"
                disabled={busy || !accountId}
                onClick={openPallet}
                sx={{ minHeight: 48 }}
              >
                Открыть палету
              </Button>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <TsdScanCapture
            disabled={busy || !accountId}
            onScan={resolveScan}
            helperText={scanHelper}
            dialogPlaceholder={scanHelper}
          />
        </Paper>

        <ToggleButtonGroup
          exclusive
          fullWidth
          value={tab}
          onChange={(_event, value) => {
            if (value) {
              setTab(value);
              closePopup();
            }
          }}
        >
          {TABS.map((item) => (
            <ToggleButton key={item.key} value={item.key} sx={{ minHeight: 48 }}>
              {item.label}
              {' '}
              ({counts[item.key] || 0})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Stack spacing={1}>
          {queueLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !queue.length ? (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              Пусто
            </Typography>
          ) : (
            queue.map((row) => (
              <Paper
                key={`${row.task?.wb_order_id || row.task?.posting_number}-${row.status}`}
                variant="outlined"
                sx={{ p: 1.5, pointerEvents: 'none' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {!labelingProducts(row.task, marketplace) && (
                    <ProductThumb task={row.task} marketplace={marketplace} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ProductIdentity task={row.task} marketplace={marketplace} />
                  </Box>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </Stack>

      <Dialog
        open={popupOpen && Boolean(task)}
        onClose={() => { /* только кнопки */ }}
        disableEscapeKeyDown
        disableAutoFocus
        disableEnforceFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <ProductIdentity task={task} marketplace={marketplace} align="center" titleVariant="titleMedium" detail />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
            {!labelingProducts(task, marketplace) && (
              <ProductThumb task={task} marketplace={marketplace} />
            )}
            {(labelingStatus === 'awaiting_confirm' || labelingStatus === 'print_pending') && (
              <Typography color="text.secondary" align="center">
                {printError
                  ? 'Не удалось отправить на печать. Распечатай повторно или отложи.'
                  : (isYandex && task?.cargo_place_current?.code
                    ? `Наклей этикетку грузоместа ${task.cargo_place_current.code}. Если не вышла — распечатай повторно.`
                    : 'Наклей этикетку. Если не вышла — распечатай повторно.')}
              </Typography>
            )}
            {labelingStatus !== 'awaiting_verify'
              && labelingStatus !== 'awaiting_confirm'
              && labelingStatus !== 'print_pending' && (
              <Typography color="text.secondary" align="center">
                Подтверди действие
              </Typography>
            )}
            {labelingStatus === 'awaiting_verify' && (
              <TsdScanCapture
                capture={false}
                disabled={busy}
                onScan={(code) => {
                  setVerifyCode(code);
                  setError('');
                }}
                helperText={verifyCode.trim()
                  ? 'Этикетка считана — нажми «На палету»'
                  : (isYandex
                    ? `Сканируй этикетку грузоместа${task?.cargo_place_current?.code ? ` ${task.cargo_place_current.code}` : ''}`
                    : 'Сканируй этикетку заказа — затем «На палету»')}
                label="Ввести ШК вручную"
                dialogTitle="Ручной ввод этикетки"
                dialogPlaceholder="Штрихкод этикетки"
              />
            )}
            {palletNotice && popupOpen && (
              <Alert severity="warning" sx={{ width: '100%' }} onClose={() => setPalletNotice('')}>
                {palletNotice}
              </Alert>
            )}
            {error && popupOpen && (
              <Alert severity="error" sx={{ width: '100%' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={busy || (labelingStatus === 'awaiting_verify' && !verifyCode.trim())}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} /> : labelingConfirmLabel(labelingStatus, marketplace, task)}
          </Button>
          {retryable && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={onConfirm}
              disabled={busy}
              sx={{ minHeight: 56 }}
            >
              Повторить
            </Button>
          )}
          {showReprint && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={reprint}
              disabled={busy}
              sx={{ minHeight: 56 }}
            >
              Распечатать повторно
            </Button>
          )}
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={closePopup}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {labelingStatus === 'awaiting_label' ? 'Отмена' : 'Отложить'}
          </Button>
          {canCancel && labelingStatus !== 'awaiting_label' && (
            <Button
              fullWidth
              variant="text"
              color="error"
              onClick={() => setDropFlowOpen(true)}
              disabled={busy}
              sx={{ minHeight: 48 }}
            >
              Снять с проклейки
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PalletCloseDialog
        open={closeOpen}
        pallet={activePallet}
        step={closeStep}
        onStepChange={setCloseStep}
        busy={busy}
        error={closeError}
        onClearError={() => setCloseError('')}
        externalScanMode
        scannedCode={closeVerifyCode}
        onScannedCodeChange={setCloseVerifyCode}
        onReprint={reprintPalletLabel}
        canPrint={Boolean(printerUid)}
        onCloseConfirm={closeActivePallet}
        onCancel={cancelCloseDialog}
      />

      <Dialog
        open={Boolean(palletSwitch)}
        onClose={() => { if (!busy) setPalletSwitch(null); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Другая палета</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {`Заказ несовместим с палетой ${palletSwitch?.pallet?.code || ''} (склад или тип груза). Открыть для него новую палету?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={confirmPalletSwitch}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} /> : 'Использовать другую'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => setPalletSwitch(null)}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dropFlowOpen}
        onClose={() => { if (!busy) setDropFlowOpen(false); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Снять заказ с проклейки?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Заказ уйдёт во вкладку «С ошибками». Вернуть его сможет начальник смены.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={dropFromLabeling}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} /> : 'Снять с проклейки'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => setDropFlowOpen(false)}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelPalletOpen}
        onClose={() => { if (!busy) setCancelPalletOpen(false); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Убрать палету {activePallet?.code || ''}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Палета пустая. Она исчезнет из работы — в погрузку её добавить будет нельзя.
            Действие необратимо.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={cancelActivePallet}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} /> : 'Убрать палету'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => setCancelPalletOpen(false)}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            Отмена
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
